import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Text, Flex, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, useDisclosure, Table, Thead, Tbody,
  Tr, Th, Td, Badge, Spinner
} from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, UnitInterestResponse } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface UnitSizeDemandDonutProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';
const MAX_BARS = 10;

type ClientEntry = { conversation_id: string; client_name: string };
type ClientsCache = Record<string, ClientEntry[]>;

const UnitSizeDemandDonut: React.FC<UnitSizeDemandDonutProps> = ({ businessIdentifier }) => {
  const [data, setData] = useState<UnitInterestResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [clients, setClients] = useState<ClientEntry[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsCache, setClientsCache] = useState<ClientsCache>({});

  const [preloaded, setPreloaded] = useState(false);

  useEffect(() => {
    if (!businessIdentifier) { setIsLoading(false); return; }
    setPreloaded(false);
    setClientsCache({});

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getUnitInterestDistribution(businessIdentifier);
        setData(result);
        setIsLoading(false); // Show chart immediately

        // Preload in background
        if (result.unit_types && result.unit_types.length > 0) {
          const topTypes = result.unit_types.slice(0, MAX_BARS).filter(t => !t.unit_type.startsWith('Others'));
          const cache: ClientsCache = {};
          const promises = topTypes.map(async (t) => {
            try {
              const r = await analyticsService.getUnitTypeClients(businessIdentifier, t.unit_type);
              cache[t.unit_type] = r.clients || [];
            } catch { /* ignore */ }
          });
          await Promise.allSettled(promises);
          setClientsCache(cache);
          setPreloaded(true);
        } else {
          setPreloaded(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch unit size demand data');
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier]);

  const hasRealClients = useCallback((unitType: string): boolean => {
    const cached = clientsCache[unitType];
    if (!cached || cached.length === 0) return false;
    return cached.some(c => !c.client_name.startsWith('Client ('));
  }, [clientsCache]);

  const handleBarClick = useCallback(async (unitType: string) => {
    if (!businessIdentifier || unitType.startsWith('Others')) return;
    if (!preloaded) return;
    if (!hasRealClients(unitType)) return;

    setSelectedUnit(unitType);
    onOpen();

    if (clientsCache[unitType]) {
      setClients(clientsCache[unitType].filter(c => !c.client_name.startsWith('Client (')));
      setIsLoadingClients(false);
      return;
    }

    setIsLoadingClients(true);
    setClients([]);
    try {
      const result = await analyticsService.getUnitTypeClients(businessIdentifier, unitType);
      const allClients = result.clients || [];
      setClientsCache(prev => ({ ...prev, [unitType]: allClients }));
      const namedClients = allClients.filter(c => !c.client_name.startsWith('Client ('));
      if (namedClients.length === 0) { onClose(); return; }
      setClients(namedClients);
    } catch { /* ignore */ } finally {
      setIsLoadingClients(false);
    }
  }, [businessIdentifier, clientsCache, hasRealClients, preloaded, onOpen, onClose]);

  const getChartOption = () => {
    if (!data || !data.unit_types || data.unit_types.length === 0) return {};
    const sorted = [...data.unit_types].sort((a, b) => b.count - a.count);
    let displayData = sorted;
    if (sorted.length > MAX_BARS) {
      const top = sorted.slice(0, MAX_BARS - 1);
      const rest = sorted.slice(MAX_BARS - 1);
      const othersCount = rest.reduce((sum, e) => sum + e.count, 0);
      const totalCount = sorted.reduce((sum, e) => sum + e.count, 0);
      displayData = [...top, { unit_type: `Others (${rest.length})`, count: othersCount, percentage: Math.round((othersCount / totalCount) * 1000) / 10 }];
    }
    const reversed = [...displayData].reverse();
    const names = reversed.map(e => e.unit_type);
    const counts = reversed.map(e => e.count);

    return {
      tooltip: {
        trigger: 'axis' as const, axisPointer: { type: 'shadow' as const }, textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const p = params[0];
          const total = data.unit_types.reduce((s, e) => s + e.count, 0);
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : '0';
          const clickable = preloaded && !p.name.startsWith('Others') && hasRealClients(p.name);
          return `<b>${p.name}</b><br/>Mentions: ${p.value}<br/>Share: ${pct}%${clickable ? '<br/><i style="color:#718096">Click to see clients</i>' : ''}`;
        },
      },
      grid: { top: 10, right: 60, bottom: 20, left: 10, containLabel: true },
      xAxis: { type: 'value' as const, axisLabel: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: '#EDF2F7', type: 'dashed' as const } } },
      yAxis: { type: 'category' as const, data: names, axisLabel: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#4A5568', width: 120, overflow: 'truncate' as const }, axisLine: { show: false }, axisTick: { show: false } },
      series: [{
        type: 'bar',
        data: counts.map((count, idx) => ({
          value: count,
          itemStyle: { color: names[idx].startsWith('Others') ? '#CBD5E0' : CHART_COLORS.palette[((reversed.length - 1) - idx) % CHART_COLORS.palette.length], borderRadius: [0, 4, 4, 0] },
        })),
        barMaxWidth: 24,
        label: { show: true, position: 'right' as const, formatter: (params: any) => { const total = data.unit_types.reduce((s, e) => s + e.count, 0); const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0'; return `${params.value} (${pct}%)`; }, fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096' },
      }],
    };
  };

  const hasData = data && data.unit_types && data.unit_types.length > 0;

  return (
    <>
      <ChartCard title="Unit Size Demand Distribution" subtitle={preloaded ? "Click any bar to see client names" : "Loading client data..."} isLoading={isLoading} error={error}>
        {hasData ? (
          <ReactECharts option={getChartOption()} style={{ height: '340px', width: '100%', cursor: 'pointer' }} opts={{ renderer: 'svg' }} onEvents={{ click: (params: any) => { if (params.name) handleBarClick(params.name); } }} />
        ) : (
          <Flex align="center" justify="center" minH="200px">
            <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>{!businessIdentifier ? 'Select a project to view data' : 'No data available'}</Text>
          </Flex>
        )}
      </ChartCard>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent>
          <ModalHeader fontFamily={FONT_FAMILY} fontSize="md">Clients interested in <Badge colorScheme="blue" fontSize="sm" ml={1}>{selectedUnit}</Badge></ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} maxH="400px" overflowY="auto">
            {isLoadingClients ? (
              <Flex justify="center" py={8}><Spinner /></Flex>
            ) : clients.length === 0 ? (
              <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>No clients found for this unit type.</Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead><Tr><Th>#</Th><Th>Client Name</Th></Tr></Thead>
                <Tbody>
                  {clients.map((client, idx) => (
                    <Tr key={client.conversation_id}><Td>{idx + 1}</Td><Td fontWeight="medium">{client.client_name}</Td></Tr>
                  ))}
                </Tbody>
              </Table>
            )}
            <Text fontSize="xs" color="gray.400" mt={3} textAlign="right">{clients.length} client{clients.length !== 1 ? 's' : ''} found</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UnitSizeDemandDonut;
